import type { DataGridColumnSettings } from '@myosh/odin-components';
import type { FieldSorts } from '../@types/common';
import type { FilterCustomProperties } from '../@types/form-filters';
import { linkedFieldsFormat } from './filter-url-builder-utils';
import { forceAssert } from './type-guards';

export const buildSortedMyActivitiesUrl = (sortedFields: FieldSorts = {}, columns: DataGridColumnSettings[] = []) => {
  let sortedFieldsUrl = '';
  const sortKeys = Object.keys(sortedFields);
  if (sortKeys.length > 0 && columns.length > 0) {
    const listOfColumnsWithSortFilters = [];
    for (let i = 0, length = columns.length; i < length; i++) {
      const sortKeyIndex = sortKeys.indexOf(columns[i].field);
      if (sortKeyIndex > -1) {
        listOfColumnsWithSortFilters.push({
          ascending: sortedFields[columns[i].field].direction,
          order: sortedFields[columns[i].field].order,
          sortField: sortedFields[columns[i].field].customDataProperties?.sortField,
        });
      }
    }

    const orderedListOfColumnsWithSortFilters = listOfColumnsWithSortFilters.sort((a, b) => a.order - b.order);
    for (let i = 0, length = orderedListOfColumnsWithSortFilters.length; i < length; i++) {
      const { ascending, sortField } = orderedListOfColumnsWithSortFilters[i];
      const sortOrder = ascending === 'asc' ? '' : '-';
      if (sortField) {
        sortedFieldsUrl += `&sort_directive=${sortOrder}${sortField}`;
      }
    }
    return sortedFieldsUrl;
  }
};

/**
 * Constructs the URL sort parameter value.
 *
 * @param sortedFields The field sorts provided by the data grid.
 * @param columns The data grid column configuration
 * @param isAllForms Boolean indicating if the function is run on All Forms view
 *
 * @returns Returns the formatted URL sort parameter value prepended with an `&`
 */
export const buildSortedFieldsUrlString = (
  sortedFields: FieldSorts = {},
  columns: DataGridColumnSettings[] = [],
  isAllForms = false,
  useSort = false
) => {
  let sortedFieldsUrl = '';
  const queryParameter = useSort ? 'sort' : 'sort_directive';
  const sortKeys = Object.keys(sortedFields);
  if (sortKeys.length > 0 && columns.length > 0) {
    const listOfColumnsWithSortFilters = [];
    for (let i = 0, length = columns.length; i < length; i++) {
      const {
        entityType,
        entityPropertyName,
        linkedFieldId,
        linkedEntityType: linkedFieldSubType,
        grandScoreFieldItemType: grandScoreSubType,
        questionRecordListFieldTypes: questionnaireSubType,
      } = forceAssert<FilterCustomProperties>(columns[i].customDataProperties);
      const fieldSubType = grandScoreSubType ?? questionnaireSubType ?? linkedFieldSubType;
      const linkedFieldFormat = linkedFieldsFormat(linkedFieldSubType, linkedFieldId, entityPropertyName);

      const sortKeyIndex = sortKeys.indexOf(columns[i].field);
      if (sortKeyIndex > -1) {
        listOfColumnsWithSortFilters.push({
          ascending: sortedFields[columns[i].field].direction,
          sortEntityType: columns[i]?.customDataProperties?.sortEntityType ?? '',
          entityId: columns[i]?.customDataProperties?.entityId ?? '',
          order: sortedFields[columns[i].field].order,
          entityType,
          entityPropertyName,
          linkedFieldFormat,
          fieldSubType,
        });
      }
    }

    const orderedListOfColumnsWithSortFilters = listOfColumnsWithSortFilters.sort((a, b) => a.order - b.order);
    for (let i = 0, length = orderedListOfColumnsWithSortFilters.length; i < length; i++) {
      const { ascending, sortEntityType, entityType, entityId, linkedFieldFormat, fieldSubType, entityPropertyName } =
        orderedListOfColumnsWithSortFilters[i];
      const sortOrder = ascending === 'asc' ? '' : '-';
      if (sortEntityType) {
        if ((entityType === 'RECORD_LINK' || entityType === 'REVERSE_RECORD_LINK') && entityId) {
          let subType = fieldSubType;
          if (subType === 'HIERARCHY_TYPE') {
            subType = 'hierarchy';
          }
          const entityTypeValue = entityType === 'RECORD_LINK' ? 'recordlink' : 'reversedrecordlink';
          const key = `${entityTypeValue}:${entityId}:${subType}:${linkedFieldFormat}`;
          sortedFieldsUrl += `&${queryParameter}=${sortOrder}${key}`;
        } else if (!isAllForms && entityType === 'KEYWORD') {
          const keywordValue = useSort ? entityId : entityPropertyName;
          sortedFieldsUrl += `&${queryParameter}=${sortOrder}${sortEntityType}:${keywordValue}`;
        } else if (!isAllForms && entityType === 'GRAND_SCORE' && entityId) {
          const key = `${entityType.replace('_', '')}:${entityId}:${fieldSubType}`;
          sortedFieldsUrl += `&${queryParameter}=${sortOrder}${key}`;
        } else if (entityType === 'QUESTIONNAIRE' && entityId) {
          const key = `${entityType}:${entityId}:${fieldSubType}`;
          sortedFieldsUrl += `&${queryParameter}=${sortOrder}${key}`;
        } else if (entityType === 'WORKFLOW_STEP' && entityId) {
          sortedFieldsUrl += `&${queryParameter}=${sortOrder}workflowstep:${entityId}:TRANSITIONDATE`;
        } else {
          sortedFieldsUrl += `&${queryParameter}=${sortOrder}${sortEntityType}:${entityId}`;
        }
      }
    }
  }

  return sortedFieldsUrl;
};

export const createCustomSortWithOrderBy = (rules?: FieldSorts) => {
  if (!rules) {
    return '';
  }

  let sortKeys = '';
  let sortDirections = '';
  for (const rule in rules) {
    const direction = rules[rule].direction.toUpperCase();
    if (sortKeys.length !== 0) {
      sortKeys = sortKeys + ',' + rule;
      sortDirections = sortDirections + ',' + direction;
    } else {
      sortKeys = rule;
      sortDirections = direction;
    }
  }
  return `&sort=${sortKeys}&order_by=${sortDirections}`;
};

export function sortingUsersApiUrl(sorts?: FieldSorts) {
  if (!sorts) {
    return '';
  }

  const sortKeys = Object.keys(sorts);
  const sortRules: Array<string> = [];
  for (let i = 0, length = sortKeys.length; i < length; i++) {
    const sortKey = sortKeys[i];
    const sortValue = sorts[sortKey];
    const sortOrder = sortValue.direction.toUpperCase() === 'DESC' ? '-' : '';
    //Regular field
    let sortRule = `${sortOrder}${sortKey}`;
    //hierarchy field
    if (sortValue.customDataProperties?.entityType === 'HIERARCHY_TYPE') {
      sortRule = `${sortOrder}HIERARCHY:${sortValue.customDataProperties?.entityId}`;
    }
    sortRules.push(sortRule);
  }

  return `&sort=${sortRules.join(',')}`;
}

export function createFieldSortText(sorts?: FieldSorts) {
  if (!sorts) {
    return '';
  }

  let sortUrlString = '';

  const sortKeys = Object.keys(sorts);
  for (let i = 0, length = sortKeys.length; i < length; i++) {
    const sortKey = sortKeys[i];
    const keySortOrder = sorts[sortKey].direction.toUpperCase();
    sortUrlString += sortUrlString.length > 0 ? `,${sortKey}:${keySortOrder}` : `${sortKey}:${keySortOrder}`;
  }

  return `&sort=${sortUrlString}`;
}
